import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import './config/axios'; // Configure axios defaults
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage } from './components/LoginPage';

// Import Lucide icons
import { 
  LayoutDashboard, Code2, Code, Target, Calendar, Repeat, TestTube2, 
  FileText, Settings, Users, BarChart3, Upload, Database,
  Plus, ChevronLeft, ChevronRight, Menu, X, Sparkles, TrendingUp, Clock,
  AlertCircle, CheckCircle2, Loader2, Network, Bot, Brain,
  DollarSign, Shield, PlayCircle, BookOpen, Server, Layers,
  Activity, Building, Package, FileCheck, Cpu, GitBranch,
  Wallet, Workflow, Play, RotateCcw, Zap, Smartphone, Factory
} from 'lucide-react';

// Import components
import { ProjectDashboard } from './components/ProjectDashboard';
import { CustomCodeAssistant } from './components/CustomCodeAssistant';
import { ImpactAnalyzer } from './components/ImpactAnalyzer';
import { WorkplanOrchestrator } from './components/WorkplanOrchestrator';
import { FioriPathfinder } from './components/FioriPathfinder';
import { TestIntelligence } from './components/TestIntelligence';
import { ReleaseNotesMiner } from './components/ReleaseNotesMiner';
import { Analytics } from './components/Analytics';
import { IntegrationSettings } from './components/IntegrationSettings';
import { FileUpload } from './components/FileUpload';
import { Configuration } from './components/Configuration';
import { GlobalDashboard } from './components/GlobalDashboard';
import { AgenticStudio } from './components/AgenticStudio';
import { SAPLandscape } from './components/SAPLandscape';
import { SmartPlanning } from './components/SmartPlanning';
import { DiscoveryEngine } from './components/DiscoveryEngine';
import { CutoverStudio } from './components/CutoverStudio';
import { CostEstimation } from './components/CostEstimation';
import { GanttTimeline } from './components/GanttTimeline';
import { Documentation } from './components/Documentation';
import { DataMigrationPlanner } from './components/DataMigrationPlanner';
import SAPSimulation from './components/SAPSimulation';
import PhaseNavigator from './components/PhaseNavigator';
import LandscapeDiscovery from './components/phase1/LandscapeDiscovery';
import ReadinessCheckOrchestrator from './components/phase1/ReadinessCheckOrchestrator';
import SimplificationMapping from './components/phase1/SimplificationMapping';
import ProcessMiningFitGap from './components/phase1/ProcessMiningFitGap';
import ArchitectureAdvisor from './components/phase1/ArchitectureAdvisor';
import CostEstimator from './components/phase1/CostEstimator';
import RiskDashboard from './components/phase1/RiskDashboard';
import ReportGenerator from './components/reports/ReportGenerator';
import SPDDSPAUPredictor from './components/phase2/SPDDSPAUPredictor';
import ABAPCodeAnalyzer from './components/phase2/ABAPCodeAnalyzer';
import AddOnCompatibility from './components/phase2/AddOnCompatibility';
import UnicodeCompliance from './components/phase2/UnicodeCompliance';
import SUMDMODigitalTwin from './components/phase3/SUMDMODigitalTwin';
import DowntimePredictor from './components/phase3/DowntimePredictor';
import UniversalJournalReadiness from './components/phase4/UniversalJournalReadiness';
import BusinessPartnerMigration from './components/phase4/BusinessPartnerMigration';
import MigrationObjectCatalog from './components/phase5/MigrationObjectCatalog';
import DataLoadWorkbench from './components/phase5/DataLoadWorkbench';
import AITestGenerator from './components/phase6/AITestGenerator';
import E2ETestComposer from './components/phase6/E2ETestComposer';
import RegressionAutomation from './components/phase6/RegressionAutomation';
import MockConversionSimulator from './components/phase7/MockConversionSimulator';
import RollbackFramework from './components/phase7/RollbackFramework';
import HypercareCenter from './components/phase8/HypercareCenter';
import PerformanceOptimizer from './components/phase8/PerformanceOptimizer';
import FioriAdoption from './components/phase8/FioriAdoption';
import TransportIntelligence from './components/phase3/TransportIntelligence';
import FinanceConversion from './components/phase4/FinanceConversion';
import LogisticsSimplification from './components/phase4/LogisticsSimplification';
import PPDSOptimizer from './components/phase4/PPDSOptimizer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Landing Page
const LandingPage = ({ onGetStarted }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              AURA
            </h1>
            <p className="text-xs text-gray-500 font-medium">AI Upgrade & Readiness Assistant</p>
          </div>
        </div>
        <button
          onClick={onGetStarted}
          data-testid="get-started-btn"
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl hover:shadow-xl transition-all duration-300 font-medium transform hover:scale-105"
        >
          Get Started →
        </button>
      </div>
    </nav>

    <div className="max-w-7xl mx-auto px-4 py-20">
      <div className="text-center mb-20">
        <div className="inline-block mb-4">
          <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">🚀 AI-Powered Migration</span>
        </div>
        <h2 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Illuminate your path from <br />
          <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">ECC</span> to{' '}
          <span className="bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">S/4HANA</span>
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          AI-powered migration intelligence platform designed to enable enterprises
          to migrate with confidence, speed, and predictability.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mt-16">
        {[
          { icon: Database, title: 'Discovery & Analysis', desc: 'Automated landscape discovery and impact analysis', color: 'blue' },
          { icon: Sparkles, title: 'AI-Powered Insights', desc: 'LLM-driven code analysis and remediation suggestions', color: 'purple' },
          { icon: TrendingUp, title: 'Smart Planning', desc: 'Intelligent workplan generation and risk assessment', color: 'green' },
          { icon: TestTube2, title: 'Test Intelligence', desc: 'AI-generated test cases based on change patterns', color: 'orange' },
          { icon: Repeat, title: 'Fiori Mapping', desc: 'Automated transaction to Fiori app mapping', color: 'pink' },
          { icon: BarChart3, title: 'Real-time Tracking', desc: 'Live dashboards and KPI monitoring', color: 'indigo' }
        ].map((feature, idx) => {
          const Icon = feature.icon;
          return (
            <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group hover:-translate-y-2">
              <div className={`w-14 h-14 bg-gradient-to-br from-${feature.color}-500 to-${feature.color}-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-md`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

// Sidebar Component
const Sidebar = ({ activeView, onNavigate, collapsed, onToggle, currentProject }) => {
  const [expandedPhases, setExpandedPhases] = useState(['phase1', 'phase7']); // Expand Phase 1 and Phase 7 by default

  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => 
      prev.includes(phaseId) 
        ? prev.filter(p => p !== phaseId)
        : [...prev, phaseId]
    );
  };

  const phases = [
    {
      id: 'core',
      name: 'Core Modules',
      color: 'blue',
      icon: LayoutDashboard,
      modules: [
        { id: 'dashboard', label: 'Project Dashboard', icon: LayoutDashboard },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
        { id: 'reports', label: 'Report Generator', icon: FileText },
        { id: 'workplan', label: 'Workplan', icon: Calendar },
      ]
    },
    {
      id: 'phase1',
      name: 'Phase 1: Pre-Assessment',
      color: 'blue',
      icon: Database,
      modules: [
        { id: 'sap-simulation', label: 'SAP Connection', icon: Server },
        { id: 'landscape-discovery', label: 'Landscape Discovery', icon: Database },
        { id: 'readiness-check', label: 'Readiness Check', icon: Shield },
        { id: 'simplification-mapping', label: 'Simplification Mapping', icon: Layers },
        { id: 'process-mining', label: 'Process Mining', icon: Activity },
        { id: 'architecture-advisor', label: 'Architecture Advisor', icon: Building },
        { id: 'cost', label: 'Cost Estimation', icon: DollarSign },
        { id: 'risk', label: 'Risk Dashboard', icon: Shield },
      ]
    },
    {
      id: 'phase2',
      name: 'Phase 2: System Preparation',
      color: 'purple',
      icon: Code,
      modules: [
        { id: 'code-assistant', label: 'ABAP Code Analyzer', icon: Code2 },
        { id: 'spdd-spau', label: 'SPDD/SPAU Predictor', icon: Code },
        { id: 'addon-compatibility', label: 'Add-On Compatibility', icon: Package },
        { id: 'unicode-compliance', label: 'Unicode Compliance', icon: FileCheck },
      ]
    },
    {
      id: 'phase3',
      name: 'Phase 3: Technical Conversion',
      color: 'pink',
      icon: Cpu,
      modules: [
        { id: 'sum-dmo-twin', label: 'SUM/DMO Digital Twin', icon: Cpu, patent: true },
        { id: 'downtime-predictor', label: 'Downtime Predictor', icon: Clock },
        { id: 'transport-intelligence', label: 'Transport Intelligence', icon: GitBranch, patent: true },
      ]
    },
    {
      id: 'phase4',
      name: 'Phase 4: Functional Conversion',
      color: 'green',
      icon: Wallet,
      modules: [
        { id: 'universal-journal', label: 'Universal Journal', icon: Wallet },
        { id: 'finance-conversion', label: 'Finance Conversion', icon: DollarSign },
        { id: 'bp-migration', label: 'BP Migration', icon: Users, patent: true },
        { id: 'logistics-simplification', label: 'Logistics Simplification', icon: Package },
        { id: 'pp-ds-optimizer', label: 'PP/DS Optimizer', icon: Factory },
      ]
    },
    {
      id: 'phase5',
      name: 'Phase 5: Data Migration',
      color: 'orange',
      icon: Database,
      modules: [
        { id: 'migration-catalog', label: 'Migration Catalog', icon: BookOpen },
        { id: 'data-migration', label: 'Data Migration Planner', icon: Database },
        { id: 'data-load-workbench', label: 'Data Load Workbench', icon: Workflow, patent: true },
      ]
    },
    {
      id: 'phase6',
      name: 'Phase 6: Testing',
      color: 'cyan',
      icon: TestTube2,
      modules: [
        { id: 'ai-test-generator', label: 'AI Test Generator', icon: TestTube2 },
        { id: 'e2e-test-composer', label: 'E2E Test Composer', icon: TestTube2 },
        { id: 'tests', label: 'Regression Automation', icon: Repeat },
      ]
    },
    {
      id: 'phase7',
      name: 'Phase 7: Cutover & Go-Live',
      color: 'red',
      icon: PlayCircle,
      modules: [
        { id: 'cutover', label: 'Cutover Studio 2.0', icon: PlayCircle },
        { id: 'gantt', label: 'Gantt Timeline', icon: TrendingUp },
        { id: 'mock-conversion', label: 'Mock Conversion', icon: Play },
        { id: 'rollback-framework', label: 'Rollback Framework', icon: RotateCcw },
      ]
    },
    {
      id: 'phase8',
      name: 'Phase 8: Hypercare',
      color: 'indigo',
      icon: Activity,
      modules: [
        { id: 'hypercare-center', label: 'Hypercare Center', icon: Activity, patent: true },
        { id: 'performance-optimizer', label: 'Performance Optimizer', icon: Zap },
        { id: 'fiori-adoption', label: 'Fiori Adoption', icon: Smartphone },
      ]
    },
    {
      id: 'settings',
      name: 'Settings & Tools',
      color: 'gray',
      icon: Settings,
      modules: [
        { id: 'documentation', label: 'Documentation', icon: BookOpen },
        { id: 'upload', label: 'File Upload', icon: Upload },
        { id: 'integrations', label: 'Integrations', icon: Settings },
        { id: 'config', label: 'Configuration', icon: Settings },
      ]
    }
  ];

  return (
    <div className={`${collapsed ? 'w-20' : 'w-64'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col h-screen sticky top-0 shadow-sm`}>
      {/* Sidebar Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">AURA</h1>
              <p className="text-xs text-gray-500">v1.0</p>
            </div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {collapsed ? <Menu className="w-5 h-5 text-gray-600" /> : <X className="w-5 h-5 text-gray-600" />}
        </button>
      </div>

      {/* Project Info */}
      {currentProject && !collapsed && (
        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Current Project</p>
          <p className="text-sm font-semibold text-gray-900 truncate">{currentProject.name}</p>
          <div className="mt-2 flex items-center space-x-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
              {currentProject.status}
            </span>
            <span className="text-xs text-gray-500">{currentProject.migration_type}</span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {phases.map((phase) => {
          const PhaseIcon = phase.icon;
          const isExpanded = expandedPhases.includes(phase.id);
          
          return (
            <div key={phase.id} className="mb-2">
              {/* Phase Header */}
              <button
                onClick={() => togglePhase(phase.id)}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-50 transition-all"
              >
                <div className="flex items-center space-x-2">
                  {!collapsed && (
                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  )}
                  <PhaseIcon className="w-4 h-4" />
                  {!collapsed && (
                    <span className="text-xs font-bold text-gray-900">{phase.name}</span>
                  )}
                </div>
                {!collapsed && (
                  <span className="text-xs text-gray-500">{phase.modules.length}</span>
                )}
              </button>
              
              {/* Phase Modules */}
              {isExpanded && !collapsed && (
                <div className="ml-4 space-y-1 mt-1">
                  {phase.modules.map((module) => {
                    const ModuleIcon = module.icon;
                    const isActive = activeView === module.id;
                    
                    return (
                      <button
                        key={module.id}
                        onClick={() => onNavigate(module.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all text-sm ${
                          isActive
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <ModuleIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                          <span className="text-xs truncate">{module.label}</span>
                        </div>
                        {module.patent && (
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            isActive ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-700'
                          }`}>P</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        {!collapsed ? (
          <div className="text-xs text-gray-500 space-y-1">
            <p>© 2025 AURA Platform</p>
            <p className="font-medium text-gray-700">ResolveTech Solutions</p>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-8 h-8 bg-gray-100 rounded-lg" />
          </div>
        )}
      </div>
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppContent() {
  const { user, loading, isAuthenticated } = useAuth();
  const [currentView, setCurrentView] = useState('globalDashboard');
  const [selectedProject, setSelectedProject] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeModule, setActiveModule] = useState('dashboard');

  // Show login page if not authenticated
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const handleGetStarted = () => {
    setCurrentView('globalDashboard');
  };

  const handleBackToLanding = () => {
    setCurrentView('landing');
  };

  const handleViewDocumentation = () => {
    setCurrentView('documentation');
  };

  const handleBackToGlobalDashboard = () => {
    setCurrentView('globalDashboard');
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setCurrentView('project');
    setActiveModule('dashboard');
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setCurrentView('globalDashboard');
  };

  const handleNavigate = (moduleId) => {
    setActiveModule(moduleId);
  };

  return (
    <div className="App min-h-screen bg-gray-50">
      {currentView === 'globalDashboard' && (
        <GlobalDashboard 
          onSelectProject={handleSelectProject} 
          onBackToHome={handleBackToLanding}
          onViewDocumentation={handleViewDocumentation}
        />
      )}
      
      {currentView === 'documentation' && (
        <Documentation 
          project={null} 
          onBackToGlobalDashboard={handleBackToGlobalDashboard}
          onBackToHome={handleBackToLanding}
        />
      )}
      
      {currentView === 'projectList' && (
        <ProjectList onSelectProject={handleSelectProject} />
      )}
      
      {currentView === 'project' && selectedProject && (
        <div className="flex h-screen overflow-hidden">
          <Sidebar
            activeView={activeModule}
            onNavigate={handleNavigate}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            currentProject={selectedProject}
          />
          <div className="flex-1 overflow-y-auto">
            <ProjectWorkspace
              project={selectedProject}
              activeModule={activeModule}
              onBack={handleBackToProjects}
              onNavigate={handleNavigate}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Project List Component
const ProjectList = ({ onSelectProject }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (projectData) => {
    try {
      const response = await axios.post(`${API}/projects`, projectData);
      await axios.post(`${API}/mock-data/generate/${response.data.id}`);
      await fetchProjects();
      setShowCreateModal(false);
      return response.data;
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Migration Projects</h1>
            <p className="text-gray-600">Manage your SAP ECC to S/4HANA migration projects</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            data-testid="create-project-btn"
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-xl transition-all duration-300 font-medium flex items-center space-x-2 transform hover:scale-105"
          >
            <Plus className="w-5 h-5" />
            <span>New Project</span>
          </button>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
            <Database className="w-20 h-20 mx-auto mb-6 text-gray-300" />
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">No projects yet</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">Create your first SAP migration project to get started with AURA's intelligent analysis</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium inline-flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create First Project</span>
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                onClick={() => onSelectProject(project)}
                data-testid={`project-card-${project.id}`}
                className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-1 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{project.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                  </div>
                  <span className={
                    `px-3 py-1 rounded-full text-xs font-semibold ${
                      project.status === 'completed' ? 'bg-green-100 text-green-700' :
                      project.status === 'execution' ? 'bg-blue-100 text-blue-700' :
                      project.status === 'planning' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`
                  }>
                    {project.status}
                  </span>
                </div>
                <div className="space-y-3 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <Repeat className="w-4 h-4 mr-2" />
                      Migration
                    </span>
                    <span className="font-semibold text-gray-900 capitalize">{project.migration_type}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Source</span>
                    <span className="font-medium text-gray-900">{project.source_system}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Target</span>
                    <span className="font-medium text-gray-900">{project.target_system}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createProject}
        />
      )}
    </div>
  );
};

// Create Project Modal
const CreateProjectModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    migration_type: 'brownfield',
    source_system: 'ECC 6.0 EHP8',
    target_system: 'S/4HANA 2023',
    owner_id: 'user-001'
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    await onCreate(formData);
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Create Migration Project</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Project Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="e.g., Global ECC to S/4HANA Migration"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              rows="4"
              placeholder="Brief description of the migration project"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Migration Type *</label>
            <select
              value={formData.migration_type}
              onChange={(e) => setFormData({ ...formData, migration_type: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="brownfield">Brownfield (System Conversion)</option>
              <option value="bluefield">Bluefield (Selective Data Transition)</option>
              <option value="greenfield">Greenfield (New Implementation)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Source System *</label>
              <select
                value={formData.source_system}
                onChange={(e) => setFormData({ ...formData, source_system: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option>ECC 6.0 EHP8</option>
                <option>ECC 6.0 EHP7</option>
                <option>ECC 6.0 EHP6</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Target System *</label>
              <select
                value={formData.target_system}
                onChange={(e) => setFormData({ ...formData, target_system: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option>S/4HANA 2024</option>
                <option>S/4HANA 2023</option>
                <option>S/4HANA 2022</option>
                <option>S/4HANA 2021</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {creating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Create Project</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Project Workspace
const ProjectWorkspace = ({ project, activeModule, onBack, onNavigate }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-8 py-6">
          <button
            onClick={onBack}
            className="text-blue-600 hover:text-blue-700 mb-3 flex items-center space-x-2 font-medium transition-colors group"
          >
            <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Projects</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
              <p className="text-gray-600 mt-2">{project.description}</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-xl text-sm font-semibold capitalize flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>{project.status}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        {activeModule === 'dashboard' && <ProjectDashboard project={project} />}
        {activeModule === 'sap-simulation' && <SAPSimulation project={project} />}
        {activeModule === 'landscape-discovery' && <LandscapeDiscovery project={project} />}
        {activeModule === 'readiness-check' && <ReadinessCheckOrchestrator project={project} />}
        {activeModule === 'simplification-mapping' && <SimplificationMapping project={project} />}
        {activeModule === 'process-mining' && <ProcessMiningFitGap project={project} />}
        {activeModule === 'architecture-advisor' && <ArchitectureAdvisor project={project} />}
        {activeModule === 'cost' && <CostEstimator project={project} />}
        {activeModule === 'risk' && <RiskDashboard project={project} />}
        {activeModule === 'code-assistant' && <ABAPCodeAnalyzer project={project} />}
        {activeModule === 'spdd-spau' && <SPDDSPAUPredictor project={project} />}
        {activeModule === 'addon-compatibility' && <AddOnCompatibility project={project} />}
        {activeModule === 'unicode-compliance' && <UnicodeCompliance project={project} />}
        {activeModule === 'sum-dmo-twin' && <SUMDMODigitalTwin project={project} />}
        {activeModule === 'downtime-predictor' && <DowntimePredictor project={project} />}
        {activeModule === 'transport-intelligence' && <TransportIntelligence project={project} />}
        {activeModule === 'universal-journal' && <UniversalJournalReadiness project={project} />}
        {activeModule === 'bp-migration' && <BusinessPartnerMigration project={project} />}
        {activeModule === 'finance-conversion' && <FinanceConversion project={project} />}
        {activeModule === 'logistics-simplification' && <LogisticsSimplification project={project} />}
        {activeModule === 'pp-ds-optimizer' && <PPDSOptimizer project={project} />}
        {activeModule === 'migration-catalog' && <MigrationObjectCatalog project={project} />}
        {activeModule === 'data-load-workbench' && <DataLoadWorkbench project={project} />}
        {activeModule === 'ai-test-generator' && <AITestGenerator project={project} />}
        {activeModule === 'e2e-test-composer' && <E2ETestComposer project={project} />}
        {activeModule === 'regression-automation' && <RegressionAutomation project={project} />}
        {activeModule === 'mock-conversion' && <MockConversionSimulator project={project} />}
        {activeModule === 'rollback-framework' && <RollbackFramework project={project} />}
        {activeModule === 'hypercare-center' && <HypercareCenter project={project} />}
        {activeModule === 'performance-optimizer' && <PerformanceOptimizer project={project} />}
        {activeModule === 'fiori-adoption' && <FioriAdoption project={project} />}
        {activeModule === 'planning' && <SmartPlanning project={project} />}
        {activeModule === 'discovery' && (
          <DiscoveryEngine 
            project={project} 
            onNavigateToLandscape={() => onNavigate('landscape')} 
          />
        )}
        {activeModule === 'agents' && <AgenticStudio project={project} />}
        {activeModule === 'landscape' && <SAPLandscape project={project} />}
        {activeModule === 'data-migration' && <DataMigrationPlanner project={project} />}
        {activeModule === 'workplan' && <WorkplanOrchestrator project={project} />}
        {activeModule === 'cutover' && <CutoverStudio project={project} />}
        {activeModule === 'gantt' && <GanttTimeline project={project} />}
        {activeModule === 'tests' && <TestIntelligence project={project} />}
        {activeModule === 'analytics' && <Analytics project={project} />}
        {activeModule === 'reports' && <ReportGenerator project={project} />}
        {activeModule === 'documentation' && <Documentation project={project} />}
        {activeModule === 'upload' && <FileUpload project={project} />}
        {activeModule === 'integrations' && <IntegrationSettings project={project} />}
        {activeModule === 'config' && <Configuration project={project} />}
      </div>
    </div>
  );
};
